import React from 'react';
import { useNavigate } from 'react-router-dom';
import ShipmentBadge from './ShipmentBadge';

/**
 * ReconciliationAlertPanel
 *
 * Displays the top critical items that need immediate action.
 * Max 5 items. Each shows: orderId, issue type badge, time-since, actions.
 *
 * Issue types (derived before passing in):
 *   orphaned_payment  — paid >15 min, no shipment started
 *   shipment_failed   — permanent_failed
 *   shipment_retrying — failed_reconciling
 *   stuck_processing  — processing >10 min
 *   sla_breach        — SLA deadline passed
 *
 * Props:
 *   alerts      Array<{ order, issueType, minutesSince }>
 *   onRetry     (order) => void  — opens retry modal
 *   onReconcile () => void       — triggers reconciliation run
 *   strings     i18n (strings.reconciliationMonitor)
 */

const ISSUE_CONFIG = {
  orphaned_payment:  { label: 'Orphaned Payment',  color: 'bg-purple-100 text-purple-800', icon: '💳' },
  shipment_failed:   { label: 'Permanent Failed',  color: 'bg-red-100 text-red-800',       icon: '❌' },
  shipment_retrying: { label: 'Retrying',          color: 'bg-orange-100 text-orange-800', icon: '🔄' },
  stuck_processing:  { label: 'Stuck Processing',  color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
  sla_breach:        { label: 'SLA Breached',      color: 'bg-orange-100 text-orange-900', icon: '⚠️' },
};

function IssueBadge({ type }) {
  const cfg = ISSUE_CONFIG[type] || { label: type, color: 'bg-gray-100 text-gray-800', icon: '?' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-sans font-medium ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

export default function ReconciliationAlertPanel({ alerts = [], onRetry, onReconcile, strings: s = {} }) {
  const navigate = useNavigate();

  if (alerts.length === 0) {
    return (
      <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-2xl text-sm font-sans text-green-800">
        <span>✅</span>
        <span>{s.noAlerts || 'No critical data integrity issues — all systems consistent'}</span>
      </div>
    );
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-heading text-red-800 flex items-center gap-2">
          <span>🚨</span>
          {s.alertsTitle || 'Critical Issues — Needs Immediate Attention'}
          <span className="px-2 py-0.5 bg-red-200 text-red-800 rounded-full text-xs font-sans font-medium">
            {alerts.length}
          </span>
        </h2>
        {onReconcile && (
          <button
            onClick={onReconcile}
            className="text-xs font-sans font-medium text-red-700 hover:text-red-900 underline transition-colors"
          >
            {s.runReconciliation || 'Run reconciliation now'}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {alerts.slice(0, 5).map(({ order, issueType, minutesSince }) => {
          const canRetry = order.shiprocket?.status === 'permanent_failed'
            || order.shiprocket?.status === 'failed_reconciling';

          return (
            <div
              key={`${order._id}-${issueType}`}
              className="flex flex-wrap items-center gap-3 bg-white border border-red-200 rounded-xl px-4 py-3"
            >
              {/* Order ID */}
              <span className="font-sans text-sm font-semibold text-headingText min-w-[110px]">
                {order.orderId}
              </span>

              {/* Issue badge */}
              <IssueBadge type={issueType} />

              {/* Shipment status */}
              {order.shiprocket?.status && (
                <ShipmentBadge status={order.shiprocket.status} showTooltip={false} />
              )}

              {/* Time since */}
              <span className="text-xs font-sans text-textSecondary">
                {minutesSince != null ? `${minutesSince}m ago` : ''}
              </span>

              {/* Actions */}
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={() => navigate(`/admin/orders?search=${order.orderId}`)}
                  className="text-xs font-sans text-secondary hover:underline transition-colors"
                >
                  {s.actionView || 'View order'}
                </button>
                {canRetry && onRetry && (
                  <button
                    onClick={() => onRetry(order)}
                    className="text-xs font-sans font-semibold text-red-700 hover:text-red-900 border border-red-300 hover:border-red-500 px-2 py-0.5 rounded-lg transition-colors"
                  >
                    {s.actionRetry || 'Retry'}
                  </button>
                )}
                {onReconcile && (
                  <button
                    onClick={onReconcile}
                    className="text-xs font-sans text-orange-700 hover:text-orange-900 border border-orange-300 hover:border-orange-500 px-2 py-0.5 rounded-lg transition-colors"
                  >
                    {s.actionReconcile || 'Reconcile'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {alerts.length > 5 && (
        <p className="mt-3 text-xs font-sans text-red-700 text-center">
          {s.alertsOverflow
            ? s.alertsOverflow.replace('{n}', alerts.length - 5)
            : `+${alerts.length - 5} more — use filters below to see all`}
        </p>
      )}
    </div>
  );
}
